import { FC } from 'react';
import { Button } from '@/components/ui/button';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSearchParams } from 'react-router-dom';

interface PaginationProps {
  totalPages: number;
  totalElements?: number;
  className?: string;
}

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];

export const Pagination: FC<PaginationProps> = ({
  totalPages,
  className = '',
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = Number(searchParams.get('size')) || 20;
  const currentPage = Number(searchParams.get('page')) || 1;
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const handlePageChange = (page: number | string) => {
    const pageNumber = Number(page);
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      setSearchParams((prev) => {
        prev.set('page', String(page));
        return prev;
      });
    }
  };

  const handleSizeChange = (value: string) => {
    setSearchParams((prev) => {
      prev.set('size', value);
      return prev;
    });
  };

  return (
    <div className={`flex items-center justify-between w-full ${className}`}>
      <div className="flex items-center justify-between w-full gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm">{t('page.size')}:</span>
          <Select value={String(pageSize)} onValueChange={handleSizeChange}>
            <SelectTrigger className="w-[70px] h-7 bg-white">
              <SelectValue placeholder={String(pageSize)} />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size, i) => (
                <SelectItem value={String(size)} key={i}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              className="size-7 p-0 border-gray-100"
              onClick={() => handlePageChange(1)}
              disabled={!canGoPrevious}
              aria-label="First page"
            >
              <ChevronsLeft size={21} />
            </Button>
            <Button
              variant="outline"
              className="size-7 p-0 border-gray-100"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!canGoPrevious}
              aria-label="Previous page"
            >
              <ChevronLeft size={18} />
            </Button>

            <Button
              className="size-7 p-0 border-gray-100 bg-gray-smoke text-midnight-default cursor-default"
              aria-label={`Current page ${currentPage}`}
            >
              {currentPage}
            </Button>

            <Button
              variant="outline"
              className="size-7 p-0 border-gray-100"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!canGoNext}
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </Button>
            <Button
              variant="outline"
              className="size-7 p-0 border-gray-100"
              onClick={() => handlePageChange(totalPages)}
              disabled={!canGoNext}
              aria-label="Last page"
            >
              <ChevronsRight size={21} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
